import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Wrench, Package, ArrowLeft } from "lucide-react";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [value, setValue] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => toast.error("Listing not found"));
  }, [id]);

  if (!listing) return <div className="py-20 text-center font-display font-extrabold text-xl animate-pulse">Loading…</div>;

  const isService = listing.type === "service";
  const isOwner = user && user.user_id === listing.owner_id;

  const submitOffer = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!desc.trim() || !value) { toast.error("Describe your offer and its value"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/offers", {
        listing_id: listing.id, offer_description: desc, offered_value_usd: parseFloat(value), message,
      });
      toast.success("Offer sent! Opening trade room…");
      setOpen(false);
      navigate(`/trade/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send offer");
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 font-bold text-sm mb-6 hover:text-[#B8860B]">
        <ArrowLeft size={16} strokeWidth={3} /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/3] bg-[#F4F4F5] nb-border rounded-2xl overflow-hidden nb-shadow">
            {listing.images?.[active] ? (
              <img src={fileUrl(listing.images[active])} alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-black/20">
                {isService ? <Wrench size={72} /> : <Package size={72} />}
              </div>
            )}
          </div>
          {listing.images?.length > 1 && (
            <div className="flex gap-3 mt-3">
              {listing.images.map((im, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${active === i ? "border-[#E6B31E]" : "border-black/20"}`}>
                  <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-xs font-extrabold nb-border rounded-full px-3 py-1 flex items-center gap-1 ${isService ? "bg-black text-[#E6B31E]" : "bg-[#E6B31E] text-black"}`}>
              {isService ? <Wrench size={12} strokeWidth={3} /> : <Package size={12} strokeWidth={3} />}
              {isService ? "Service" : "Good"}
            </span>
            <span className="text-xs font-bold uppercase tracking-wide text-[#B8860B]">{listing.category}</span>
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter">{listing.title}</h1>
          <div className="mt-3 text-2xl font-display font-extrabold">Est. value ${Number(listing.value_usd).toLocaleString()}</div>

          <p className="mt-5 text-black/70 font-medium leading-relaxed whitespace-pre-wrap">{listing.description}</p>

          <div className="mt-6 bg-[#FBF7E9] nb-border rounded-xl p-5">
            <div className="text-xs font-bold uppercase text-black/50">Wants in exchange</div>
            <div className="font-display font-extrabold text-lg mt-1">{listing.wanted}</div>
          </div>

          <div className="mt-5 flex items-center justify-between text-sm font-semibold text-black/60">
            <span>Listed by <Link to={`/user/${listing.owner_id}`} data-testid="owner-link" className="text-[#B8860B] font-bold hover:underline">{listing.owner_name}</Link></span>
            {listing.location && <span className="flex items-center gap-1"><MapPin size={14} strokeWidth={3} /> {listing.location}</span>}
          </div>

          {listing.status === "traded" ? (
            <div className="mt-6 font-extrabold text-lg text-black/50">This item has already been traded.</div>
          ) : isOwner ? (
            <div className="mt-6 font-bold text-black/50">This is your listing.</div>
          ) : (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="open-offer-btn" className="mt-6 w-full bg-[#E6B31E] text-black font-extrabold text-lg rounded-full px-6 py-4 border-2 border-black nb-shadow nb-hover">
                  Send trade offer
                </button>
              </DialogTrigger>
              <DialogContent className="border-2 border-black rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display font-extrabold text-2xl">Propose your trade</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-bold">What are you offering?</label>
                    <textarea data-testid="offer-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
                      placeholder="e.g. My vintage film camera + 2 lenses" className="w-full mt-1 p-3 nb-border rounded-xl font-medium outline-none focus:ring-2 focus:ring-[#E6B31E]" />
                  </div>
                  <div>
                    <label className="text-sm font-bold">Estimated value (USD)</label>
                    <input data-testid="offer-value-input" type="number" value={value} onChange={(e) => setValue(e.target.value)}
                      placeholder="300" className="w-full mt-1 p-3 nb-border rounded-xl font-medium outline-none focus:ring-2 focus:ring-[#E6B31E]" />
                  </div>
                  <div>
                    <label className="text-sm font-bold">Message (optional)</label>
                    <textarea data-testid="offer-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
                      placeholder="Say hi and explain your offer…" className="w-full mt-1 p-3 nb-border rounded-xl font-medium outline-none focus:ring-2 focus:ring-[#E6B31E]" />
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="submit-offer-btn" disabled={submitting} onClick={submitOffer}
                    className="w-full bg-black text-[#E6B31E] font-extrabold rounded-full px-6 py-3 disabled:opacity-50">
                    {submitting ? "Sending…" : "Send offer & open chat"}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  );
}
